import React, { useMemo, useState } from 'react'
import { FaSearch } from 'react-icons/fa'
import { Bar, ChipSelect, Empty, Pill, Seg } from './fieldUi'
import { useField } from './fieldContext'
import { agendaFor, dayOf } from '../store'
import { addDays, fmtDate, fmtShort, money, pct, todayIso, weekday } from '../FieldOpsKit'
import { clockOfStamp } from '../geo'
import { VISIT_LABEL, VISIT_TONE } from './stops'

/**
 * History: past routes (one card per day, tap for the map and every stop) and the list of past visits (salesperson) or
 * deliveries (driver). Everything here is read-only.
 */
export default function HistoryScreen() {
    const f = useField()
    const { state, nav, actor } = f
    const isDriver = actor.kind === 'driver'
    const [view, setView] = useState('days')
    const [range, setRange] = useState('30')
    const [res, setRes] = useState('')
    const [q, setQ] = useState('')
    const [limit, setLimit] = useState(25)
    const today = todayIso()

    // ---- days ----
    const days = useMemo(() => {
        const out = []
        for (let i = 1; i <= 42 && out.length < 24; i++) {
            const d = addDays(today, -i)
            if (weekday(d) === 0 || weekday(d) === 6) continue
            if (!isDriver) {
                const ag = agendaFor(state.sales, actor.rep, d)
                if (ag.length === 0) continue
                const done = ag.filter((x) => x.visit.status === 'done')
                const missed = ag.filter((x) => x.visit.status === 'missed' || x.visit.status === 'planned').length
                const orders = state.sales.orders.filter((o) => o.rep === actor.rep && o.date === d).reduce((a, o) => a + o.total, 0)
                out.push({ date: d, planned: ag.length, done: done.length, missed: missed, orders: orders, rec: dayOf(state.field, actor.key, d), lastIn: done.length ? done[done.length - 1].visit.checkOutAt : null })
            } else {
                const list = state.drivers.deliveries.filter((x) => x.driverId === actor.driverId && x.date === d && x.status !== 'cancelled')
                if (list.length === 0) continue
                const done = list.filter((x) => x.status === 'delivered')
                out.push({ date: d, planned: list.length, done: done.length, missed: list.length - done.length, orders: done.reduce((a, x) => a + x.amount, 0), rec: dayOf(state.field, actor.key, d) })
            }
        }
        return out
    }, [state.sales, state.drivers, state.field.days, actor.key]) // eslint-disable-line react-hooks/exhaustive-deps

    // ---- visits / deliveries ----
    const fromDate = range === 'all' ? '0000-00-00' : addDays(today, -Number(range))
    const items = useMemo(() => {
        if (isDriver) {
            return state.drivers.deliveries.filter((x) => x.driverId === actor.driverId && x.date < today && x.date >= fromDate && x.status !== 'cancelled').sort((a, b) => (a.date < b.date ? 1 : -1))
        }
        const sMap = {}
        state.sales.stores.forEach((s) => { sMap[s.id] = s })
        return state.sales.visits.filter((v) => v.rep === actor.rep && v.date < today && v.date >= fromDate && (v.status === 'done' || v.status === 'missed')).map((v) => ({ v: v, s: sMap[v.storeId] })).sort((a, b) => (a.v.date < b.v.date ? 1 : -1))
    }, [state.sales, state.drivers, range, actor.key]) // eslint-disable-line react-hooks/exhaustive-deps
    const filtered = items.filter((x) => {
        const name = isDriver ? x.customer + ' ' + x.invoiceNo : x.s.name + ' ' + x.s.city
        if (q.trim() && name.toLowerCase().indexOf(q.trim().toLowerCase()) < 0) return false
        if (!res) return true
        if (isDriver) return res === 'delivered' ? x.status === 'delivered' : x.status !== 'delivered'
        return res === 'missed' ? x.v.status === 'missed' : x.v.status === 'done' && x.v.outcome === res
    })
    const doneCount = isDriver ? 0 : items.filter((x) => x.v.status === 'done').length
    const orderCount = isDriver ? 0 : items.filter((x) => x.v.status === 'done' && x.v.outcome === 'order').length

    return (
        <div className="fo-stack">
            <Seg full value={view} onChange={setView} items={[{ key: 'days', label: isDriver ? 'My days' : 'My routes' }, { key: 'items', label: isDriver ? 'Deliveries' : 'Visits' }]} />
            {view === 'days' ? (
                days.length === 0 ? <Empty title="No past routes yet" text="Your finished days will appear here." /> : (
                    <div className="fo-stack" style={{ gap: 10 }}>
                        {days.map((d) => (
                            <button key={d.date} type="button" className="fo-dayrow" onClick={() => nav.push({ type: 'day', date: d.date })}>
                                <div className="fo-dayrow__top">
                                    <div><strong>{fmtDate(d.date)}</strong><span>{d.rec && d.rec.startedAt ? clockOfStamp(d.rec.startedAt) + ' – ' + (d.rec.endedAt ? clockOfStamp(d.rec.endedAt) : '…') : 'No day record'}</span></div>
                                    {d.rec ? (d.rec.submitted ? <Pill tone="green">Report sent</Pill> : <Pill tone="amber">Report not sent</Pill>) : <Pill tone="gray">—</Pill>}
                                </div>
                                <Bar value={d.done} max={d.planned} tone={pct(d.done, d.planned) >= 90 ? 'green' : pct(d.done, d.planned) >= 60 ? 'blue' : 'amber'} />
                                <div className="fo-dayrow__nums">
                                    <div><span>{isDriver ? 'Delivered' : 'Visits'}</span><strong>{d.done}/{d.planned}</strong></div>
                                    <div><span>{pct(d.done, d.planned)}%</span><strong>{d.missed ? d.missed + ' ' + (isDriver ? 'not delivered' : 'missed') : 'Complete'}</strong></div>
                                    <div><span>{isDriver ? 'Invoiced' : 'Orders'}</span><strong>{money(d.orders)}</strong></div>
                                    <div><span>Miles</span><strong>{d.rec && d.rec.miles ? d.rec.miles : '—'}</strong></div>
                                </div>
                            </button>
                        ))}
                    </div>
                )
            ) : (
                <React.Fragment>
                    {!isDriver ? <div className="fo-grid3"><div className="fo-stat"><span>Visits</span><strong>{items.length}</strong></div><div className="fo-stat"><span>With order</span><strong>{pct(orderCount, doneCount)}%</strong><small>{orderCount} of {doneCount}</small></div><div className="fo-stat"><span>Missed</span><strong style={{ color: '#DC2626' }}>{items.length - doneCount}</strong></div></div> : null}
                    <div className="fo-searchbox"><FaSearch /><input className="fo-input" placeholder={isDriver ? 'Search customer or invoice' : 'Search store or city'} value={q} onChange={(e) => { setQ(e.target.value); setLimit(25) }} aria-label="Search history" /></div>
                    <Seg full value={range} onChange={(v) => { setRange(v); setLimit(25) }} items={[{ key: '7', label: '7 days' }, { key: '30', label: '30 days' }, { key: 'all', label: 'All' }]} />
                    <div className="fo-catscroll"><ChipSelect value={res} onChange={(v) => { setRes(v); setLimit(25) }} options={isDriver ? [{ key: 'delivered', label: 'Delivered' }, { key: 'other', label: 'Not delivered' }] : [{ key: 'order', label: 'Order taken' }, { key: 'no_order', label: 'No order' }, { key: 'closed', label: 'Closed' }, { key: 'missed', label: 'Missed' }]} /></div>
                    {filtered.length === 0 ? <Empty title="Nothing found" text="Change the filters to see more." /> : (
                        <div className="fo-stack" style={{ gap: 8 }}>
                            {filtered.slice(0, limit).map((x) => isDriver ? (
                                <button key={x.id} type="button" className="fo-visitrow" onClick={() => nav.push({ type: 'delivery', id: x.id })}>
                                    <div><strong>{x.customer}</strong><span>{x.invoiceNo} · {fmtShort(x.deliveredOn || x.date)} · {x.city}</span></div>
                                    <div className="fo-visitrow__r"><strong>{money(x.amount)}</strong><Pill tone={x.status === 'delivered' ? 'green' : 'gray'}>{x.status === 'delivered' ? (x.shortLines && x.shortLines.length ? 'Partial' : 'Delivered') : x.status}</Pill></div>
                                </button>
                            ) : (
                                <button key={x.v.id} type="button" className="fo-visitrow" onClick={() => nav.push({ type: 'visit', id: x.v.id })}>
                                    <div><strong>{x.s.name}</strong><span>{fmtShort(x.v.date)} · {x.v.checkInAt ? clockOfStamp(x.v.checkInAt) : x.v.time || ''}{x.v.dur ? ' · ' + x.v.dur + ' min' : ''} · {x.v.reason || x.v.missReason || x.s.city}</span></div>
                                    <div className="fo-visitrow__r">{x.v.status === 'missed' ? <Pill tone="red">Missed</Pill> : <Pill tone={VISIT_TONE[x.v.outcome] || 'green'}>{VISIT_LABEL[x.v.outcome]}</Pill>}{x.v.soNo ? <small>{x.v.soNo}</small> : null}</div>
                                </button>
                            ))}
                            {filtered.length > limit ? <button type="button" className="fo-btn fo-btn--ghost fo-btn--block" onClick={() => setLimit(limit + 25)}>Show more ({filtered.length - limit} left)</button> : null}
                        </div>
                    )}
                </React.Fragment>
            )}
        </div>
    )
}
